import React, { FC, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Wei } from '../../../utils/types'
import {
  defaultSlippage,
  maxDepositSlippage,
  whiteListedDepositToken,
  wmatic,
} from '../../../config'
import { MultiDepositPreview } from './MultiDepositPreview'
import { isTokenNative, Token } from '../../../logic/token/token'
import { Pool, poolContainsToken } from '../../../logic/pools/Pool'
import { SwaapLargeLoader } from '../../catalog/SwaapLoader'
import { get0xQuote, ZeroExApi } from '../../../api/ox/ox.api'
import { PoolApi } from '../../../api/web3/pool-api'
import { PoolWithData, ZeroExQuote } from '../../../api/api-types'
import { Web3Context } from '../../../hooks/web3'
import { fromWei } from '../../../utils/static-web3'
import { oracleApi } from '../../../api/oracle/oracle-client'
import { ExitApi } from '../../../api/web3/exit-api'
import { MINIMAL_VALUE_FOR_TOKEN_PRECISION } from './OneAssetPanel'
import { removeSlippage } from '../../../logic/weis/wei-calculate'

const OneAssetPreviewWrapper = styled.div``

export interface JoinWithSwaapArgs {
  pool: Pool
  token: Token
  amount: Wei
  expectedShare: Wei
  minimumShare: Wei
  amountsIn: Map<Token, Wei> // Warning, with no slippage
}
export interface JoinWith0xArgs extends JoinWithSwaapArgs {
  fillQuotes: ZeroExQuote[]
}

export interface Props {
  token: Token
  amount: Wei
  pool: PoolWithData
  shareSlippage: number
  onDepositReady: (
    tradeWithSwaap: boolean,
    args: JoinWithSwaapArgs | JoinWith0xArgs
  ) => void
  // In the rare case where it's impossible to trade with 0x nor Swaap
  onDepositError: (message: string | undefined) => void
}
const ERRORS = {
  MINIMAL: 'Too small amount',
  UNEXPECTED: 'Transaction cancelled',
  ZERO_EX: 'Unable to trade with 0x',
}
const ALL_ERRORS = Object.values(ERRORS)

export const OneAssetPreview: FC<Props> = ({
  token,
  amount,
  pool,
  shareSlippage,
  onDepositReady,
  onDepositError,
}) => {
  const { web3, account, appChainID, proxy } = useContext(Web3Context)
  const [ready, setReady] = useState<any>(false)

  const [expectedShare, setExpectedShare] = useState<Wei | undefined>(undefined)
  const [minimumShare, setMinimumShare] = useState<Wei | undefined>(undefined)
  const [amountsIn, setAmountsIn] = useState<Map<Token, Wei>>(new Map())
  const [use0x, setUse0x] = useState<boolean>(false)

  useEffect(() => {
    chooseMethod()
      .then(() => setReady(true))
      .catch((e: any) => {
        setReady(true)

        if (ALL_ERRORS.includes(e)) {
          onDepositError(e)
        } else {
          onDepositError('Error calculating shares')
        }
      })
  }, [])

  async function chooseMethod(): Promise<void> {
    // Obviously if the token is not in the Pool, we won't swap with Swaap
    if (!poolContainsToken(pool, token)) {
      setUse0x(true)
      return await calculateShareWith0x()
    }

    await oracleApi.getPrices()
    const MINIMAL_VALUE_FOR_0X = 1 // one dollar

    const SHARE_THRESHOLD = 0 // If lower, we use Swaap. If bigger, we use 0x

    const joinValue =
      oracleApi.getPrice(token.symbol) * fromWei(amount, token.decimals)
    const poolApi = new PoolApi(web3!, account!, pool.address)
    let use0x = false

    if (joinValue < MINIMAL_VALUE_FOR_TOKEN_PRECISION) {
      throw ERRORS.MINIMAL
    }
    if (joinValue < MINIMAL_VALUE_FOR_0X) {
      // Not enough value for 0x, we use it internally

      const result = await poolApi.checkBalanced(token, amount)
      if (result === false) {
        throw ERRORS.MINIMAL
      }

      // Stay with Swaap
    } else {
      const result = await poolApi.checkBalanced(token, amount)

      if (result === false) {
        use0x = true
      } else {
        const relativeShare = (Number(result) / Number(pool.totalShares)) * 100
        use0x = relativeShare >= SHARE_THRESHOLD
      }
    }

    setUse0x(use0x)
    use0x ? await calculateShareWith0x() : await calculateShareWithSwaap()
  }

  /**
   * Returns expected share, or false if the pool would become unbalanced
   */
  async function calculateShareWithSwaap(): Promise<void> {
    if (!web3 || !account) {
      throw 'illegal state'
    }

    const poolApi = new PoolApi(web3, account, pool.address)
    const exitApi = new ExitApi(web3, account, appChainID, pool.address)

    const expectedShare = await poolApi.viewOneAssetJoinShare(token, amount)

    // Building tokens amounts
    const amountsIn = new Map<Token, Wei>()

    const amounts = await exitApi.viewMultiAssetExitShare(expectedShare)

    for (let i = 0; i < pool.tokens.length; i++) {
      const token = pool.tokens[i]
      const amountWei = amounts[i]
      amountsIn.set(token, amountWei)
    }
    // Saving
    setExpectedShare(expectedShare)
    setAmountsIn(amountsIn)
    let shareWithSlippage = removeSlippage(expectedShare, shareSlippage)
    setMinimumShare(shareWithSlippage)

    onDepositReady(true, {
      pool,
      token,
      amount,
      expectedShare,
      minimumShare: shareWithSlippage,
      amountsIn,
    })
  }

  /**
   * We first get a quote, then call the view function poolApi.viewMultiAssetJoinShare
   * This view function was designed to match other assets needs, when given leader asset
   *
   * Two cases: we join with MATIC, or we join with internal token like WETH
   *  - case MATIC: we call and get 3 quotes. Need to call viewMultiAssetJoinShare 3 times
   *  - case WETH: we call viewMultiAssetJoinShare with 2 tokens from quote. Then we substract
   *     the amount of WETH needed for the quote to the initial WETH amount
   */
  async function calculateShareWith0x(): Promise<void> {
    if (!web3 || !account) {
      console.error('Illegal state')
      return
    }

    let sellToken = token
    const sellAmount = amount
    if (isTokenNative(token)) {
      sellToken = wmatic
    }

    const quote = await get0xQuote(sellToken, pool, sellAmount, shareSlippage)

    const zeroExApi = new ZeroExApi(web3, account, appChainID, proxy)
    const { expectedShare, amounts } =
      await zeroExApi.getLimitingAmountsForSwap(pool, quote, token, sellAmount)

    setAmountsIn(amounts)
    const shareWithSlippage = '0'
    setExpectedShare(expectedShare)
    setMinimumShare(shareWithSlippage)
    onDepositReady(false, {
      pool,
      fillQuotes: quote,
      token,
      amount,
      expectedShare,
      minimumShare: shareWithSlippage,
      amountsIn: amounts,
    })
  }
  const exactInput = new Map<Token, Wei>().set(token, amount)

  return (
    <OneAssetPreviewWrapper>
      {ready ? (
        <MultiDepositPreview
          amounts={exactInput}
          expectedShareTokensEquivalent={use0x ? undefined : amountsIn}
          expectedShareTokensViaAggregator={use0x ? amountsIn : undefined}
          pool={pool}
          expectedShare={expectedShare}
          minimumShare={minimumShare}
          expectedShareSlippage={shareSlippage}
        />
      ) : (
        <SwaapLargeLoader />
      )}
    </OneAssetPreviewWrapper>
  )
}
